import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ChevronDown, GripVertical, X, Plus } from 'lucide-react'
import { useFieldArray, useWatch } from 'react-hook-form'
import type { Control, UseFormSetValue } from 'react-hook-form'
import type { FormValues } from './agent-detail.schema'
import { DEFAULT_INTAKE_SLOTS } from './agent-detail.schema'
import {
  type IntakeFormSlotKey,
  type IntakeSlotPolicy,
} from '@super/shared-types'
import { SLOT_LABELS, SLOT_PROMPT_DEFAULTS } from './intake-form-constants'
import { cn } from '@/lib/utils'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// Stable reference — prevents useSensor's memo from busting on every render
const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 4 } }

const AVAILABLE_SLOTS = Object.keys(SLOT_LABELS) as IntakeFormSlotKey[]

type PolicyValue = IntakeSlotPolicy

const POLICY_OPTIONS: { value: PolicyValue; title: string }[] = [
  {
    value: 'skip',
    title: "Use silently — agent won't ask or confirm this with the caller",
  },
  {
    value: 'confirm',
    title:
      'Agent has this info but will verify with the caller before using it',
  },
  {
    value: 'ask',
    title: 'Agent will collect this from the caller during the call',
  },
]

function policyStyle(active: boolean) {
  if (!active)
    return {
      bg: 'transparent',
      color: 'var(--muted-foreground)',
      fontWeight: 400,
      shadow: 'none',
    }
  return {
    bg: 'var(--background)',
    color: 'var(--foreground)',
    fontWeight: 500,
    shadow: '0 1px 2px rgba(0,0,0,0.10)',
  }
}

function PolicyPill({
  value,
  active,
  onClick,
}: {
  value: PolicyValue
  active: boolean
  onClick: () => void
}) {
  const { bg, color, fontWeight, shadow } = policyStyle(active)
  const title = POLICY_OPTIONS.find(o => o.value === value)?.title

  return (
    <button
      type="button"
      title={title}
      onClick={e => {
        e.stopPropagation()
        onClick()
      }}
      style={{
        padding: '3px 10px',
        fontSize: '12px',
        fontFamily: "'Space Grotesk', sans-serif",
        fontWeight,
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        background: bg,
        color,
        boxShadow: shadow,
        transition: 'all 0.12s',
        whiteSpace: 'nowrap',
      }}
    >
      <span className="capitalize">{value}</span>
    </button>
  )
}

function PolicyGroup({
  value,
  onChange,
}: {
  value: PolicyValue
  onChange: (v: PolicyValue) => void
}) {
  return (
    <div
      style={{
        display: 'flex',
        borderRadius: '6px',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        background: 'var(--muted)',
        padding: '2px',
        gap: '1px',
        flexShrink: 0,
      }}
    >
      {POLICY_OPTIONS.map(opt => (
        <PolicyPill
          key={opt.value}
          value={opt.value}
          active={value === opt.value}
          onClick={() => onChange(opt.value)}
        />
      ))}
    </div>
  )
}

/** Small inline badge showing the current policy value in the collapsed row */
function PolicyBadge({ value }: { value: PolicyValue }) {
  return (
    <Badge
      variant="outline"
      className="text-[10px] px-1 py-0 leading-4 capitalize"
    >
      {value}
    </Badge>
  )
}

function PromptField({
  label,
  description,
  value,
  placeholder,
  onChange,
}: {
  label: string
  description: string
  value: string
  placeholder: string
  onChange: (v: string) => void
}) {
  const isCustom = value.trim() !== ''
  return (
    <div className="space-y-1.5">
      <div>
        <span className="text-[11px] font-medium text-foreground">{label}</span>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Textarea
        className="text-xs min-h-[56px] resize-y font-mono"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] text-muted-foreground">
          {isCustom ? '↳ custom' : '↳ using Super default'}
        </span>
        {isCustom ? (
          <button
            type="button"
            className="text-[10.5px] text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => onChange('')}
          >
            Clear
          </button>
        ) : (
          <button
            type="button"
            className="text-[10.5px] text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => onChange(placeholder)}
          >
            Use as template →
          </button>
        )}
      </div>
    </div>
  )
}

type SlotRowProps = {
  index: number
  fieldId: string
  slotKey: IntakeFormSlotKey
  control: Control<FormValues>
  setValue: UseFormSetValue<FormValues>
  onRemove: () => void
}

function SlotRow({
  index,
  fieldId,
  slotKey,
  control,
  setValue,
  onRemove,
}: SlotRowProps) {
  const [expanded, setExpanded] = useState(false)
  const [showPrompts, setShowPrompts] = useState(false)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: fieldId })

  const onCrm = useWatch({ control, name: `intakeForm.slots.${index}.onCrm` })
  const onHistory = useWatch({
    control,
    name: `intakeForm.slots.${index}.onHistory`,
  })
  const confirmPrompt =
    useWatch({ control, name: `intakeForm.slots.${index}.confirmPrompt` }) ?? ''
  const askPrompt =
    useWatch({ control, name: `intakeForm.slots.${index}.askPrompt` }) ?? ''

  const defaults = SLOT_PROMPT_DEFAULTS[slotKey]

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        index !== 0 && 'border-t border-border',
        isDragging && 'opacity-50',
      )}
    >
      {/* Collapsed row — entire row is the expand toggle */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        className={cn(
          'flex items-center gap-2 py-2.5 px-1 -mx-1 cursor-pointer select-none',
          'rounded-md transition-colors hover:bg-muted/50',
        )}
        onClick={() => setExpanded(e => !e)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setExpanded(v => !v)
          }
        }}
      >
        {/* Drag handle */}
        <button
          type="button"
          className="text-muted-foreground cursor-grab active:cursor-grabbing p-0.5 rounded flex-shrink-0 touch-none"
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>

        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform flex-shrink-0',
            !expanded && '-rotate-90',
          )}
        />
        <span
          className="text-xs font-medium px-1.5 py-0.5 rounded border border-border bg-muted text-foreground flex-shrink-0"
          style={{ fontFamily: "'Fragment Mono', monospace" }}
        >
          {SLOT_LABELS[slotKey]}
        </span>

        {/* Policy summary — visible in collapsed row */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-xs text-muted-foreground">On file:</span>
          <PolicyBadge value={onCrm as PolicyValue} />
          <span className="text-xs text-muted-foreground ml-1">
            Prior call:
          </span>
          <PolicyBadge value={onHistory as PolicyValue} />
        </div>

        <div className="flex-1" />

        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onRemove()
          }}
          className="text-muted-foreground hover:text-destructive transition-colors p-0.5 rounded flex-shrink-0"
          aria-label={`Remove ${SLOT_LABELS[slotKey]}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div
          className="mb-2.5 rounded-lg p-3 space-y-3"
          style={{ background: 'var(--muted)' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Context description */}
          <p className="text-xs text-muted-foreground leading-relaxed">
            When the agent already knows this value, what should it do? Values
            can come from your CRM records (on file) or from something the
            contact mentioned in a prior conversation.
          </p>

          {/* Policy selectors */}
          <div className="space-y-2">
            {(
              [
                {
                  field: 'onCrm' as const,
                  label: 'On file (CRM)',
                  value: onCrm,
                },
                {
                  field: 'onHistory' as const,
                  label: 'Prior call',
                  value: onHistory,
                },
              ] as const
            ).map(({ field, label, value }) => (
              <div key={field} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-24 flex-shrink-0">
                  {label}
                </span>
                <PolicyGroup
                  value={value as PolicyValue}
                  onChange={v =>
                    setValue(`intakeForm.slots.${index}.${field}`, v, {
                      shouldDirty: true,
                    })
                  }
                />
              </div>
            ))}
          </div>

          {/* Prompts toggle */}
          <button
            type="button"
            onClick={() => setShowPrompts(p => !p)}
            className="text-xs"
            style={{
              color: 'var(--info-dim)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            {showPrompts ? '↑ Hide prompts' : '↓ Customize prompts'}
          </button>

          {/* Prompt fields */}
          {showPrompts && (
            <div className="space-y-3 pt-1">
              <p className="text-xs text-muted-foreground">
                Customize how the agent phrases these questions. Use{' '}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="underline decoration-dotted cursor-help font-mono">
                        {'{variables}'}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs max-w-[220px]">
                      <p className="font-medium mb-1">Available variables</p>
                      <ul className="space-y-0.5 text-muted-foreground">
                        <li>
                          <span className="font-mono">{'{first_name}'}</span> —
                          caller&apos;s first name
                        </li>
                        <li>
                          <span className="font-mono">{'{email_domain}'}</span>{' '}
                          — email domain on file
                        </li>
                        <li>
                          <span className="font-mono">{'{street_name}'}</span> —
                          property street name (single property only)
                        </li>
                        <li>
                          <span className="font-mono">{'{role}'}</span> —
                          caller&apos;s contact type
                        </li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>{' '}
                to personalize. Leave blank to use Super&apos;s default wording.
              </p>
              <PromptField
                label="Confirm prompt"
                description="What the agent says when confirming a known value."
                value={confirmPrompt ?? ''}
                placeholder={defaults.confirm}
                onChange={v =>
                  setValue(`intakeForm.slots.${index}.confirmPrompt`, v, {
                    shouldDirty: true,
                  })
                }
              />
              <PromptField
                label="Ask prompt"
                description="What the agent says when asking for a value."
                value={askPrompt ?? ''}
                placeholder={defaults.ask}
                onChange={v =>
                  setValue(`intakeForm.slots.${index}.askPrompt`, v, {
                    shouldDirty: true,
                  })
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

type Props = {
  control: Control<FormValues>
  setValue: UseFormSetValue<FormValues>
}

export function IntakeFormCard({ control, setValue }: Props) {
  const enabled = useWatch({ control, name: 'intakeForm.enabled' }) ?? false
  const slots = useWatch({ control, name: 'intakeForm.slots' }) ?? []

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'intakeForm.slots',
  })

  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex(f => f.id === active.id)
      const newIndex = fields.findIndex(f => f.id === over.id)
      move(oldIndex, newIndex)
    }
  }

  const usedSlots = new Set(slots.map((s: { slot: string }) => s.slot))
  const availableToAdd = AVAILABLE_SLOTS.filter(s => !usedSlots.has(s))

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Intake form</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Configure how the agent handles the first turns of every
              conversation, based on what it already knows about the contact.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={v => {
              setValue('intakeForm.enabled', v, { shouldDirty: true })
              if (v && slots.length === 0) {
                setValue('intakeForm.slots', DEFAULT_INTAKE_SLOTS, {
                  shouldDirty: true,
                })
              }
            }}
          />
        </div>
      </CardHeader>

      {enabled && (
        <>
          <Separator />
          <CardContent className="pt-0 pb-4 px-6">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fields.map(f => f.id)}
                strategy={verticalListSortingStrategy}
              >
                {fields.map((field, index) => (
                  <SlotRow
                    key={field.id}
                    index={index}
                    fieldId={field.id}
                    slotKey={field.slot as IntakeFormSlotKey}
                    control={control}
                    setValue={setValue}
                    onRemove={() => remove(index)}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {availableToAdd.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-3">
                {availableToAdd.map(slotKey => {
                  const defaults = DEFAULT_INTAKE_SLOTS.find(
                    s => s.slot === slotKey,
                  )
                  return (
                    <Button
                      key={slotKey}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 gap-1"
                      onClick={() =>
                        append(
                          defaults ?? {
                            slot: slotKey,
                            onCrm: 'skip',
                            onHistory: 'ask',
                          },
                        )
                      }
                    >
                      <Plus className="h-3 w-3" />
                      {SLOT_LABELS[slotKey]}
                    </Button>
                  )
                })}
              </div>
            )}
          </CardContent>
        </>
      )}
    </Card>
  )
}
